/**
 * TipTap Custom Extension: DMP Placeholder
 *
 * Renders <span class="dmp-placeholder" data-description="..."> elements
 * as highlighted, clickable inline nodes in the editor.
 */

import { Node, mergeAttributes } from '@tiptap/core';

export interface DMPPlaceholderOptions {
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    dmpPlaceholder: {
      /**
       * Insert a DMP placeholder node
       */
      insertPlaceholder: (description: string) => ReturnType;
    };
  }
}

export const DMPPlaceholderExtension = Node.create<DMPPlaceholderOptions>({
  name: 'dmpPlaceholder',

  group: 'inline',
  inline: true,
  atom: true, // Cannot be edited directly - must be replaced

  addAttributes() {
    return {
      description: {
        default: '',
        parseHTML: (element) =>
          element.getAttribute('data-description') || element.textContent?.replace('[PLACEHOLDER: ', '').replace(']', '') || '',
        renderHTML: (attributes) => ({
          'data-description': attributes.description,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span.dmp-placeholder',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(
        {
          class: 'dmp-placeholder',
          contenteditable: 'false',
        },
        HTMLAttributes
      ),
      `[PLACEHOLDER: ${HTMLAttributes['data-description']}]`,
    ];
  },

  addCommands() {
    return {
      insertPlaceholder:
        (description: string) =>
        ({ chain }) => {
          return chain()
            .insertContent({
              type: this.name,
              attrs: { description },
            })
            .run();
        },
    };
  },
});
